import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FiBookOpen, FiInbox, FiArrowRight, FiCalendar, FiDownload, FiFilter,
} from 'react-icons/fi';

import { Spinner } from '../../components/ui/Spinner';
import { Alert } from '../../components/ui/Alert';
import { PortalPage } from '../../components/layout/PortalPage';
import { SectionCard } from '../../components/layout/SectionCard';
import { getEstabelecimentoAtualId } from '../../lib/estabelecimento';
import {
  diariosOficiaisService, CADERNO_LABEL,
  type EdicaoListaItem, type Caderno,
} from '../../services/diariosOficiais.service';
import { formatarDataBR, formatarDataExtenso } from '../../lib/data';

export function DiarioOficialPublicoPage() {
  const anoAtual = new Date().getFullYear();
  const [ano, setAno] = useState(anoAtual);
  const [edicoes, setEdicoes] = useState<EdicaoListaItem[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    setCarregando(true);
    setErro(null);
    const id = getEstabelecimentoAtualId();
    diariosOficiaisService.listar(id, ano)
      .then((r) => setEdicoes(r.edicoes))
      .catch((e) => setErro(e instanceof Error ? e.message : 'Erro'))
      .finally(() => setCarregando(false));
  }, [ano]);

  const opcoesAno = [anoAtual, anoAtual - 1, anoAtual - 2, anoAtual - 3, anoAtual - 4];

  return (
    <PortalPage
      titulo="Diário Oficial Municipal"
      subtitulo="Edições oficiais publicadas."
      icone={<FiBookOpen size={26} />}
      largura="larga"
      voltarHref="/"
      voltarLabel="Voltar ao portal"
      breadcrumbs={[
        { label: 'Transparência' },
        { label: 'Diário Oficial' },
      ]}
    >
      <div className="space-y-5">
        <SectionCard titulo="Filtros" icone={<FiFilter size={18} />}>
          <div className="flex items-center gap-3 flex-wrap">
            <label className="text-sm text-slate-600">Ano:</label>
            <select
              value={ano}
              onChange={(e) => setAno(parseInt(e.target.value, 10))}
              className="h-9 px-3 rounded-md border border-slate-300 bg-white text-sm font-medium"
            >
              {opcoesAno.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
        </SectionCard>

        {carregando ? (
          <div className="flex justify-center py-16"><Spinner tamanho="lg" /></div>
        ) : erro ? (
          <Alert variante="erro">{erro}</Alert>
        ) : edicoes.length === 0 ? (
          <SectionCard>
            <div className="text-center py-12">
              <div className="w-14 h-14 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-3">
                <FiInbox className="text-slate-400" size={26} />
              </div>
              <p className="text-sm font-medium text-slate-700">Nenhuma edição publicada em {ano}.</p>
            </div>
          </SectionCard>
        ) : (
          <div className="space-y-2">
            <div className="text-xs text-slate-500">
              {edicoes.length} {edicoes.length === 1 ? 'edição publicada' : 'edições publicadas'}
            </div>
            {edicoes.map((e) => (
              <Link
                key={e.id}
                to={`/transparencia/diario-oficial/${e.id}`}
                className="block bg-white border border-slate-200 rounded-xl hover:border-slate-400 hover:shadow-sm transition"
              >
                <div className="flex items-start gap-4 p-4">
                  <div className="flex-shrink-0 mt-0.5 text-slate-400">
                    <FiBookOpen size={20} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="text-base font-semibold text-slate-900 tabular-nums">
                        {e.numero}
                      </span>
                      {e.caderno && e.caderno !== 'normal' && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 font-semibold">
                          {CADERNO_LABEL[e.caderno as Caderno]}
                        </span>
                      )}
                    </div>
                    {e.data_publicacao && (
                      <div className="text-sm text-slate-700 mt-1 capitalize">
                        {formatarDataExtenso(e.data_publicacao)}
                      </div>
                    )}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-slate-500">
                      <span>{e.atos_quantidade} {e.atos_quantidade === 1 ? 'ato' : 'atos'}</span>
                      {e.publicada_em && (
                        <span className="inline-flex items-center gap-1">
                          <FiCalendar size={11} /> Publicada em {formatarDataBR(e.publicada_em)}
                        </span>
                      )}
                      {e.pdf_url && (
                        <span className="inline-flex items-center gap-1 text-emerald-700 font-medium">
                          <FiDownload size={11} /> PDF disponível
                        </span>
                      )}
                    </div>
                  </div>
                  <FiArrowRight className="text-slate-300 flex-shrink-0 mt-1" size={16} />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </PortalPage>
  );
}
